import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import "./DesktopDownloadPage.css";

function formatBytes(bytes) {
  const numeric = Number(bytes);
  if (!Number.isFinite(numeric) || numeric <= 0) return "Unknown size";
  const units = ["B", "KB", "MB", "GB", "TB"];
  let unitIndex = 0;
  let value = numeric;
  while (value >= 1024 && unitIndex < units.length - 1) {
    value /= 1024;
    unitIndex += 1;
  }
  return `${value >= 10 || unitIndex === 0 ? value.toFixed(0) : value.toFixed(1)} ${units[unitIndex]}`;
}

function formatReleaseDate(value) {
  if (!value) return "Unknown publish date";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Unknown publish date";
  return new Intl.DateTimeFormat("en-CA", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(date);
}

export default function DesktopDownloadPage() {
  const [release, setRelease] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;

    async function loadRelease() {
      try {
        const response = await fetch("/downloads/desktop/win/x64/release.json", {
          cache: "no-store",
        });

        if (!response.ok) {
          throw new Error("The desktop release manifest is not available yet.");
        }

        const payload = await response.json();
        if (!cancelled) {
          setRelease(payload);
          setLoading(false);
        }
      } catch (nextError) {
        if (!cancelled) {
          setError(
            nextError instanceof Error
              ? nextError.message
              : "The desktop release manifest could not be loaded.",
          );
          setLoading(false);
        }
      }
    }

    void loadRelease();

    return () => {
      cancelled = true;
    };
  }, []);

  const downloadHref = useMemo(() => {
    if (!release?.installerFile) return "";
    return `/downloads/desktop/win/x64/${release.installerFile}`;
  }, [release]);

  return (
    <div className="desktop-download-page">
      <section className="desktop-download-hero">
        <div className="desktop-download-hero__kicker-row">
          <p className="desktop-download-eyebrow">Valovate Desktop</p>
          <span className="desktop-download-beta-pill">Beta testing build</span>
        </div>
        <h1>Download the Windows app for recording, VOD review, Team Stats, and guides.</h1>
        <p className="desktop-download-lede">
          Install the dedicated Valovate desktop app to record locally, upload VODs to your team,
          review recordings, work with Team Stats, and browse desktop-integrated guide content with
          built-in update support.
        </p>

        <div className="desktop-download-beta-banner">
          This release is currently intended for beta testing. It gives your team the full desktop
          workflow, but the installer and update experience may still be refined over the next
          release cycles.
        </div>

        <div className="desktop-download-riot-banner">
          <strong>Riot Games disclaimer</strong>
          <span>
            The Valovate Website was created under Riot Games&apos; &quot;Legal Jibber Jabber&quot;
            policy using assets owned by Riot Games. Riot Games does not endorse or sponsor this
            project.
          </span>
        </div>

        <div className="desktop-download-hero__actions">
          <a
            className={`desktop-download-button ${
              !loading && downloadHref ? "" : "desktop-download-button--disabled"
            }`}
            href={downloadHref || undefined}
            download
            aria-disabled={!downloadHref}
          >
            {loading ? "Checking release..." : downloadHref ? "Download for Windows" : "Release not available yet"}
          </a>
          <Link className="desktop-download-secondary" to="/terms-of-service">
            Terms of Service
          </Link>
          <Link className="desktop-download-secondary" to="/privacy-policy">
            Privacy Policy
          </Link>
        </div>

        <div className="desktop-download-summary-grid">
          <article className="desktop-download-summary-card">
            <span>Platform</span>
            <strong>Windows 10 / 11</strong>
          </article>
          <article className="desktop-download-summary-card">
            <span>Latest version</span>
            <strong>{release?.version || (loading ? "Loading..." : "Unavailable")}</strong>
          </article>
          <article className="desktop-download-summary-card">
            <span>Installer size</span>
            <strong>{formatBytes(release?.sizeBytes)}</strong>
          </article>
          <article className="desktop-download-summary-card">
            <span>Published</span>
            <strong>{formatReleaseDate(release?.releaseDate)}</strong>
          </article>
        </div>

        {error ? <div className="desktop-download-banner desktop-download-banner--error">{error}</div> : null}
      </section>

      <section className="desktop-download-section">
        <div className="desktop-download-section__heading">
          <h2>What the desktop app gives you</h2>
          <p>
            The Windows app is meant for smoother local recording workflows, cleaner desktop review,
            and easier update delivery than a browser-only flow.
          </p>
        </div>

        <div className="desktop-download-card-grid">
          <article className="desktop-download-card">
            <h3>Local-first recording</h3>
            <p>
              Save gameplay and coach recordings directly to the PC first, then upload when the
              session is over instead of forcing everything through the browser during play.
            </p>
          </article>
          <article className="desktop-download-card">
            <h3>Desktop VOD review</h3>
            <p>
              Review existing team VODs, local recordings, notes, focus points, and linked Team
              Stats without leaving the app.
            </p>
          </article>
          <article className="desktop-download-card">
            <h3>Built-in updates</h3>
            <p>
              Packaged installs can check for newer desktop releases, download them, and prompt for
              a restart once the next build is ready.
            </p>
          </article>
        </div>
      </section>

      <section className="desktop-download-section desktop-download-section--compact">
        <div className="desktop-download-section__heading">
          <h2>Install notes</h2>
        </div>

        <div className="desktop-download-note-grid">
          <div className="desktop-download-note">
            <strong>First run</strong>
            <p>
              Users must accept the current desktop Terms of Service and Privacy Policy before using
              the app, and the installer now also includes the Riot disclaimer before installation
              completes.
            </p>
          </div>
          <div className="desktop-download-note">
            <strong>Sign-in</strong>
            <p>
              The desktop app uses the same Valovate account and team access model as the website.
            </p>
          </div>
          <div className="desktop-download-note">
            <strong>Trust prompts</strong>
            <p>
              Windows may still show install trust prompts depending on the current signing status
              of the published installer.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}
